import * as React from 'react'
import { observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { Modal, Input, Button, Select, Icon, message } from 'antd'
import { TaxModels } from '@getgreenline/products'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { ServerStore } from '../../../../stores/ServerStore'
import { FormError } from '../../../../components/FormError'
import { parseErrorMsg } from '../../../../utilities/helpers'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  serverStore?: ServerStore
  onClose: () => void
  onSubmit: () => void
  taxGroup?: TaxModels.TaxGroup
  taxes: TaxModels.ITaxContract[]
}

interface State {
  isEdit?: boolean
  isSaving?: boolean
  errorMessage?: string
}

// This is a partial copy of the the CreateExpenseModal container
@inject('serverStore', 'currentCompanyStore', 'currentUserStore')
@observer
export class EditTaxGroupModal extends React.Component<Props, State> {
  taxGroupId?: number
  @observable taxGroup = new TaxModels.CreateTaxGroup()

  constructor(props: Props) {
    super(props)
    this.state = {
      isEdit: false,
      isSaving: false,
    }
  }

  componentDidMount() {
    const taxGroupId = this.props.taxGroup ? this.props.taxGroup.id : undefined
    if (taxGroupId) {
      this.taxGroupId = taxGroupId
      this.setState({ isEdit: true })
      const taxGroups = this.props.currentCompanyStore!.productStore!.taxGroups!
      const foundTaxGroup = taxGroups.find((taxGroup) => taxGroup.id === taxGroupId)
      if (taxGroupId && foundTaxGroup) {
        const updateObject = new TaxModels.CreateTaxGroup()
        updateObject.name = foundTaxGroup.name
        updateObject.taxLines = foundTaxGroup.taxLines
        this.taxGroup = updateObject
      }
    } else {
      this.taxGroup.taxLines.push({
        taxId: undefined,
        percent: undefined,
      })
    }
  }

  save = async () => {
    await this.setState({ isSaving: true })

    try {
      if (this.state.isEdit) {
        const updateObject = this.taxGroup as TaxModels.UpdateTaxGroup
        updateObject.id = this.taxGroupId!

        await this.props.currentCompanyStore!.productStore!.editTaxGroup(updateObject)
      } else {
        await this.props.currentCompanyStore!.productStore!.addTaxGroup(this.taxGroup)
      }

      this.props.onSubmit()
      message.success(`Tax group successfully ${this.state.isEdit ? 'updated' : 'created'}`, 1)
    } catch (error) {
      message.error(parseErrorMsg(error))
    } finally {
      this.setState({ isSaving: false })
    }
  }

  render() {
    let { taxes } = this.props

    if (!taxes) {
      return <span />
    }

    const country = this.props.currentCompanyStore!.company!.country

    taxes = taxes.filter((tax) => tax.country === country)

    return (
      <Modal
        maskClosable={false}
        visible={true}
        onCancel={this.props.onClose}
        title={this.state.isEdit ? 'Edit tax group' : 'Add tax group'}
        footer={[
          <Button key='cancel' onClick={this.props.onClose}>
            Cancel
          </Button>,
          <Button
            key='save'
            type='primary'
            loading={this.state.isSaving}
            onClick={() => this.save()}
          >
            Save
          </Button>,
        ]}
      >
        <div>
          <label>Tax group name</label>
          <Input
            value={this.taxGroup.name || ''}
            onChange={(e) => (this.taxGroup.name = e.target.value)}
          />
        </div>
        <br />
        <div>A tax group consists of multiple taxes.</div>
        <br />
        <div>
          {this.taxGroup.taxLines.map((taxLine, index) => {
            return (
              <Select<number | undefined>
                allowClear
                placeholder='Select tax'
                key={index}
                style={{ width: '100%', marginBottom: 5 }}
                value={taxLine ? taxLine.taxId : undefined}
                onChange={(taxId) => {
                  if (taxId) {
                    const tax = taxes.find((result) => result.id === taxId)!
                    taxLine.taxId = taxId
                    taxLine.percent = tax.percent
                  } else {
                    taxLine.taxId = undefined
                    taxLine.percent = 0
                  }
                }}
              >
                {taxes.map((tax) => {
                  return (
                    <Select.Option key={tax.id} value={tax.id}>
                      {tax.name}
                    </Select.Option>
                  )
                })}
              </Select>
            )
          })}
        </div>
        <br />
        <div>
          <Button
            style={{ marginRight: 5 }}
            onClick={() =>
              this.taxGroup.taxLines.push({
                taxId: undefined,
                percent: undefined,
              })
            }
          >
            <Icon type='plus' />
            Add tax
          </Button>
          {this.taxGroup.taxLines.length > 0 && (
            <Button onClick={() => this.taxGroup.taxLines.pop()}>
              <Icon type='minus' />
              Remove tax
            </Button>
          )}
        </div>
        <br />
        <div>
          Total tax: <b>{this.taxGroup.totalPercent}%</b>
        </div>
        <FormError message={this.state.errorMessage} />
      </Modal>
    )
  }
}
